<template>
  <div>
    <DashboardHeader :current-dashboard-state="dashboardState" />
    <div id="dashboard-element" ref="dashboardRef" class="dashboard">
      <grid-layout
        class="dashboard-grid-layout"
        :layout="layout"
        :col-num="gridColNum"
        :row-height="gridRowHeight"
        :is-draggable="isDraggable"
        :is-resizable="true"
        :vertical-compact="true"
        :margin="gridMargin"
        :use-css-transforms="true"
        :responsive="false"
      >
        <grid-item
          v-for="item in layout"
          :id="`grid-item-${item.i}-${dashboardState?.dashboard.id}`"
          :key="item.i + '-' + dashboardState?.dashboard.id"
          v-model:layout="layout"
          v-model:x="item.x"
          v-model:y="item.y"
          v-model:w="item.w"
          v-model:h="item.h"
          data-test-id="charts"
          :data-id="item.id"
          :i="item.i"
          :static="false"
          class="chart-item popup"
          drag-allow-from=".card-header"
          @init="initEvent"
          @resized="resizedEvent"
          @moved="movedEvent"
        >
          <dashboard-chart-widget
            :widget="item.widget"
            :chart-index="item.i"
            :grid-item-id="`grid-item-${item.i}-${dashboardState?.dashboard.id}`"
            :x-grid-offset="dashboardGridPositionX"
            :y-grid-offset="dashboardGridPositionY"
          />
        </grid-item>
      </grid-layout>

      <!-- focus mode mask -->
      <div v-show="showModal" class="grip-modal"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import DashboardHeader from './DashboardHeader.vue'
  import DashboardChartWidget from '@/components/DashboardChartWidget/DashboardChartWidget.vue'
  import { computed, ComputedRef, onMounted, ref, watch } from 'vue'
  import { WidgetProperties } from '@/models/dashboard.model'
  import { useDashboardStore } from '@/stores/dashboardStore'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { useProjectStore } from '@/stores/projectStore'
  import { storeToRefs } from 'pinia'
  import { debounce } from 'lodash'
  import { useGlobalFilterStore } from '@/stores/globalFilterStore'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'

  const globalFilterStore = useGlobalFilterStore()
  const dashboardStore = useDashboardStore()
  const snapshotStore = useSnapshotStore()
  const projectStore = useProjectStore()

  const dashboardService = injectTyped(ServiceKeys.dashboard)

  const dashboardRef = ref<HTMLElement | null>(null)
  const showModal = ref(false)
  const isDraggable = ref(true)
  const gridColNum = ref(30)
  const gridRowHeight = ref(10)
  const gridMargin = ref([30, 30, 30, 30])

  type Layout = Array<{
    x: number
    y: number
    w: number
    h: number
    i: number
    id: number
    hiddenSeries?: string[]
    widget: WidgetProperties
  }>

  const widgets = computed(() => dashboardState.value?.dashboard.widgets)

  const layout: ComputedRef<Layout> = computed(() =>
    (widgets.value ?? []).map((widget) => ({
      x: widget.x,
      y: widget.y,
      w: widget.width,
      h: widget.height,
      i: widget.id,
      id: widget.id,
      widget: widget,
    })),
  )

  const { selectedDashboardState: dashboardState } = storeToRefs(dashboardStore)

  const dashboardGridPositionX = computed((): number => {
    return document.getElementById('sidebar')?.offsetWidth || 0
  })

  const dashboardGridPositionY = computed((): number => {
    const headerExtensionHeight = document.getElementById('header-extension')?.offsetHeight || 0
    const headerExtensionOffsetTop = document.getElementById('header-extension')?.offsetTop || 0

    return headerExtensionHeight + headerExtensionOffsetTop
  })

  const debouncedSave = debounce(saveDashboard, 1000)

  watch(() => snapshotStore.currentSnapshot, updateWidgets)
  watch(() => dashboardStore.selectedDashboardId, updateWidgets)
  watch(() => globalFilterStore.selectedGlobalFilterId, updateWidgets)
  watch(() => dashboardStore.selectedDashboardState?.dashboardModified, debouncedSave)

  onMounted(updateWidgets)

  async function updateWidgets() {
    if (
      snapshotStore.currentSnapshot?.id !== undefined &&
      projectStore.currentProject?.id !== undefined
    ) {
      try {
        await dashboardStore.loadAvailableWidgetsFromApi({
          dashboardService: dashboardService,
          projectId: projectStore.currentProject.id,
          snapshotId: snapshotStore.currentSnapshot.id,
          filter: globalFilterStore.selectedGlobalFilter?.id,
        })
      } catch {
        //
      }
    }
  }

  function initEvent(i: string, H: number, W: number) {
    dashboardStore.updateWidgetProperties(parseInt(i), { width: W, height: H })
  }

  function resizedEvent(i: string, newH: number, newW: number) {
    dashboardStore.updateWidgetProperties(parseInt(i), { width: newW, height: newH })
  }

  function movedEvent(i: string, newX: number, newY: number) {
    dashboardStore.updateWidgetProperties(parseInt(i), { x: newX, y: newY })
  }
  async function saveDashboard(modified?: boolean): Promise<void> {
    if (modified && projectStore.currentProject?.id && snapshotStore.currentSnapshot?.id) {
      const dashboard = dashboardStore.selectedDashboardState?.dashboard

      if (!dashboard) return

      await dashboardStore.updateDashboard(
        dashboardService,
        projectStore.currentProject.id,
        dashboard,
      )
      dashboardStore.markDashboardSaved()
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  .dashboard-grid-layout {
    margin-left: -25px;
  }
  .grid {
    position: relative;
  }
  .grip-modal {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    position: fixed;
    z-index: 50;
    background-color: rgba(0, 0, 0, 0.5);
  }
  .chart-item {
    z-index: 5;
    display: block;
    border-radius: 6px;
    background-color: #fff;
    color: black;
    box-shadow: 0 0 40px rgba(226, 226, 226, 0.5);
    // overflow: hidden;
    .chart {
      width: 100%;
      height: 90%;
      margin: 0 auto;
    }

    .vue-resizable-handle {
      display: none;
    }

    &:hover {
      .vue-resizable-handle {
        display: block;
      }
    }

    &.cssTransforms {
      transition: all 200ms ease;
      transition-property: box-shadow, transform;
    }

    &:hover {
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
    }

    &.popup {
      position: absolute !important;
      z-index: 100;
    }
  }
  .dash-header {
    font-size: 30px;
    font-weight: 300;
  }
  .vue-grid-item.vue-resizable.cssTransforms.chart-item.popup {
    position: absolute;
    z-index: 1004;
  }
</style>
